import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Edit, Package, Calendar, CheckCircle, Clock, ChevronDown, X } from 'lucide-react';
import api from '../api';
import { Breadcrumbs } from './Breadcrumbs';

function ReleasePlanning({ user, onLogout }) {
  const navigate = useNavigate();
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedRelease, setExpandedRelease] = useState(null);
  const [releaseItems, setReleaseItems] = useState({});

  useEffect(() => {
    loadReleases();
  }, []);

  const loadReleases = async () => {
    setLoading(true);
    try {
      const data = await api.getReleases();
      setReleases(data);
    } catch (error) {
      console.error('Failed to load releases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRelease = async (id) => {
    if (!window.confirm('Delete this release?')) return;
    try {
      await api.deleteRelease(id);
      setReleases(releases.filter(r => r._id !== id));
    } catch (error) {
      console.error('Failed to delete release:', error);
    }
  };

  const toggleExpand = async (releaseId) => {
    if (expandedRelease === releaseId) {
      setExpandedRelease(null);
      return;
    }
    setExpandedRelease(releaseId);
    if (!releaseItems[releaseId]) {
      try {
        const items = await api.getReleaseItems(releaseId);
        setReleaseItems(prev => ({ ...prev, [releaseId]: items }));
      } catch (error) {
        console.error('Failed to load release items:', error);
        setReleaseItems(prev => ({ ...prev, [releaseId]: [] }));
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planned': return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
      case 'in-progress': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'released': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  return (
    <div className="min-h-full" data-testid="release-planning">
      {/* Breadcrumbs */}
      <div className="px-6 lg:px-8 pt-6 pb-2">
        <Breadcrumbs items={[
          { label: 'Dashboard', path: '/' },
          { label: 'Releases', path: '/releases' },
        ]} />
      </div>
      {/* Page header */}
      <div className="px-6 lg:px-8 pt-4 pb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Release Planning</h1>
            <p className="text-sm text-muted-foreground mt-1">Plan and track your product releases</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-5 py-2.5 bg-primary text-primary-foreground rounded-xl hover:opacity-90 font-semibold flex items-center gap-2 shadow-sm shadow-primary/20 btn-press transition-opacity"
            data-testid="create-release-button"
          >
            <Plus className="h-4 w-4" /> New Release
          </button>
        </div>
      </div>

      <div className="px-6 lg:px-8 pb-8">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => <div key={i} className="h-24 bg-muted rounded-xl animate-pulse" />)}
          </div>
        ) : releases.length === 0 ? (
          <div className="text-center py-16 bg-card rounded-xl border border-border">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Releases Yet</h3>
            <p className="text-muted-foreground mb-6">Create your first release to start planning</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium inline-flex items-center gap-2"
            >
              <Plus className="h-4 w-4" /> Create First Release
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {releases.map(release => (
              <div key={release._id} className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-md transition-shadow" data-testid={`release-card-${release._id}`}>
                <div className="p-5 flex items-center justify-between cursor-pointer" onClick={() => toggleExpand(release._id)}>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${expandedRelease === release._id ? 'rotate-180' : ''}`} />
                      <Package className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-lg">{release.name}</h3>
                        {release.version && <span className="text-xs font-mono bg-muted px-2 py-1 rounded-md">v{release.version}</span>}
                        <span className={`text-xs px-2 py-1 rounded-md font-medium capitalize ${getStatusColor(release.status)}`}>
                          {release.status}
                        </span>
                      </div>
                      {release.description && <p className="text-sm text-muted-foreground mt-1">{release.description}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {release.target_date && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        {new Date(release.target_date).toLocaleDateString()}
                      </div>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteRelease(release._id); }}
                      className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-colors"
                      data-testid={`delete-release-${release._id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {expandedRelease === release._id && (
                  <div className="border-t border-border p-5 bg-muted/30">
                    <h4 className="text-sm font-medium mb-3">Assigned Items</h4>
                    {!releaseItems[release._id] ? (
                      <div className="animate-pulse h-16 bg-muted rounded-lg" />
                    ) : releaseItems[release._id].length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">No items assigned to this release yet</p>
                    ) : (
                      <div className="space-y-2">
                        {releaseItems[release._id].map(item => (
                          <div key={item._id} className="flex items-center justify-between p-3 bg-card border border-border rounded-lg">
                            <div className="flex items-center gap-3">
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded uppercase ${
                                item.type === 'initiative' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                                item.type === 'epic' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                                'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                              }`}>{item.type}</span>
                              <span className="font-medium text-sm">{item.title}</span>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-md font-medium capitalize ${getStatusColor(item.status)}`}>{item.status}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateReleaseModal onClose={() => setShowCreateModal(false)} onCreated={(r) => { setReleases([...releases, r]); setShowCreateModal(false); }} />
      )}
    </div>
  );
}

function CreateReleaseModal({ onClose, onCreated }) {
  const [formData, setFormData] = useState({ name: '', version: '', description: '', target_date: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await api.createRelease(formData);
      onCreated(result);
    } catch (error) {
      console.error('Failed to create release:', error);
      alert('Failed to create release');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="create-release-modal">
      <div className="bg-card rounded-xl max-w-md w-full border border-border shadow-2xl">
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-bold flex items-center gap-2"><Package className="h-5 w-5" /> Create New Release</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Release Name *</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary" placeholder="e.g., Sprint 24" data-testid="release-name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Version</label>
            <input type="text" value={formData.version} onChange={(e) => setFormData({ ...formData, version: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary" placeholder="e.g., 2.1.0" data-testid="release-version-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary" data-testid="release-description-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Target Date</label>
            <input type="date" value={formData.target_date} onChange={(e) => setFormData({ ...formData, target_date: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary" data-testid="release-date-input" />
          </div>
          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="px-5 py-2.5 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 font-medium">
              Cancel
            </button>
            <button type="submit" className="px-5 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium shadow-sm" data-testid="submit-release">
              Create Release
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ReleasePlanning;
